import styled, {css} from 'styled-components';
import {getColorFromTheme} from '@ui/ui-utils';

export const DialogFrame = styled.section`
  background: ${getColorFromTheme('white')};
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  box-shadow: ${({theme}) => theme.elevation.level1};
  display: flex;
  flex-direction: column;
  margin: auto;
  max-height: calc(100vh - 48px);
  overflow: hidden;
  color: ${getColorFromTheme('blackTwo')};
  width: min(640px, calc(100vw - 32px));

  @media (max-width: 640px) {
    max-height: calc(100vh - 16px);
    width: calc(100vw - 16px);
  }
`;

export const DialogHeader = styled.header`
  padding: 24px 24px 20px;
`;

export const TitleRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

export const DialogTitle = styled.h2`
  color: ${getColorFromTheme('blackTwo')};
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  margin: 0;
`;

export const DialogSubtitle = styled.p`
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  line-height: 20px;
  margin: 4px 0 0;
`;

export const CloseButton = styled.button`
  align-items: center;
  background: ${getColorFromTheme('white')};
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  color: ${getColorFromTheme('blackTwo')};
  cursor: pointer;
  display: inline-flex;
  flex: none;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  height: 40px;
  padding: 0 14px;

  &:hover {
    background: ${getColorFromTheme('backgroundDark')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const Stepper = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: auto 1fr auto 1fr auto;
  margin-top: 24px;
`;

export const StepPill = styled.div<{$state: 'complete' | 'active' | 'idle'}>`
  align-items: center;
  background: ${({$state}) =>
    $state === 'idle'
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  border: 1px solid
    ${({$state}) =>
      $state === 'complete'
        ? getColorFromTheme('green300')
        : getColorFromTheme('borderLight')};
  border-radius: 999px;
  color: ${({$state}) =>
    $state === 'complete'
      ? getColorFromTheme('green400')
      : $state === 'active'
      ? getColorFromTheme('blackTwo')
      : getColorFromTheme('textBody')};
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  min-width: 118px;
  padding: 8px 14px;
  text-align: center;
  ${({$state}) =>
    $state === 'active' &&
    css`
      box-shadow: ${({theme}) => theme.elevation.level2};
    `}

  @media (max-width: 640px) {
    min-width: auto;
    padding: 7px 10px;
  }
`;

export const StepConnector = styled.div<{$complete?: boolean}>`
  background: ${({$complete}) =>
    $complete
      ? getColorFromTheme('green300')
      : getColorFromTheme('borderLight')};
  height: 1px;
  min-width: 16px;
`;

export const DialogBody = styled.div`
  border-top: 1px solid ${getColorFromTheme('borderLight')};
  flex: 1;
  overflow: auto;
  padding: 24px;
`;

export const SectionTitle = styled.h3`
  color: ${getColorFromTheme('blackTwo')};
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  margin: 0 0 4px;
`;

export const SectionDescription = styled.p`
  color: ${getColorFromTheme('textBody')};
  font-size: 13px;
  line-height: 20px;
  margin: 0 0 16px;
`;

export const FieldLabel = styled.label`
  color: ${getColorFromTheme('blackTwo')};
  display: block;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 8px;
`;

export const EmailInput = styled.div<{$error?: boolean}>`
  align-content: flex-start;
  border: 1px solid
    ${({$error}) =>
      $error ? getColorFromTheme('red400') : getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 112px;
  padding: 12px;

  &:focus-within {
    border-color: ${({$error}) =>
      $error ? getColorFromTheme('red400') : getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px
      ${({$error}) =>
        $error ? getColorFromTheme('red200') : getColorFromTheme('purple200')};
  }
`;

export const EmailChip = styled.span<{$invalid?: boolean}>`
  align-items: center;
  background: ${({$invalid}) =>
    $invalid
      ? getColorFromTheme('red100')
      : getColorFromTheme('backgroundDark')};
  border: 1px solid
    ${({$invalid}) => ($invalid ? getColorFromTheme('red400') : 'transparent')};
  border-radius: 6px;
  color: ${({$invalid}) =>
    $invalid ? getColorFromTheme('red400') : getColorFromTheme('blackTwo')};
  display: inline-flex;
  font-size: 13px;
  gap: 6px;
  height: 30px;
  padding: 0 8px;
`;

export const ChipRemove = styled.button`
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0;
`;

export const EmailDraftInput = styled.input`
  background: transparent;
  border: 0;
  color: ${getColorFromTheme('blackTwo')};
  flex: 1;
  font: inherit;
  min-width: 180px;
  outline: 0;
  padding: 5px 2px;

  &::placeholder {
    color: ${getColorFromTheme('textBody')};
    font-weight: 400;
  }
`;

export const HelperRow = styled.div`
  align-items: center;
  color: ${getColorFromTheme('textBody')};
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  line-height: 18px;
  margin-top: 8px;
`;

export const OrDivider = styled.div`
  align-items: center;
  color: ${getColorFromTheme('textBody')};
  display: flex;
  font-size: 12px;
  gap: 12px;
  margin: 20px 0;

  &::before,
  &::after {
    background: ${getColorFromTheme('borderLight')};
    content: '';
    flex: 1;
    height: 1px;
  }
`;

export const CsvCard = styled.button`
  align-items: center;
  background: ${getColorFromTheme('backgroundLight')};
  border: 1px dashed ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  color: ${getColorFromTheme('blackTwo')};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 16px;
  text-align: left;
  width: 100%;

  &:hover {
    border-color: ${getColorFromTheme('purple300')};
    background: ${getColorFromTheme('purple100')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const CsvTitle = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 600;
`;

export const CsvDescription = styled.span`
  color: ${getColorFromTheme('textBody')};
  display: block;
  font-size: 12px;
  line-height: 18px;
  margin-top: 2px;
`;

export const SummaryCard = styled.div`
  align-items: center;
  background: ${getColorFromTheme('backgroundLight')};
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 8px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 12px 14px;
`;

export const SummaryText = styled.span`
  color: ${getColorFromTheme('blackTwo')};
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TextButton = styled.button`
  background: none;
  border: 0;
  color: ${getColorFromTheme('purple300')};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  padding: 0;
`;

export const AssignmentGrid = styled.div`
  display: grid;
  gap: 24px;
`;

export const GroupSection = styled.div`
  border-top: 1px solid ${getColorFromTheme('borderLight')};
  padding-top: 24px;
`;

export const GroupSectionTitle = styled.h3`
  color: ${getColorFromTheme('blackTwo')};
  font: 600 14px/22px Graphik, sans-serif;
  margin: 0 0 4px;
`;

export const GroupSectionDescription = styled.p`
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;
  margin: 0 0 12px;
`;

export const DropdownWrap = styled.div`
  position: relative;
`;

export const SelectTrigger = styled.button<{$open?: boolean}>`
  align-items: center;
  background: ${getColorFromTheme('white')};
  border: 1px solid
    ${({$open}) =>
      $open ? getColorFromTheme('purple300') : getColorFromTheme('borderDark')};
  border-radius: 8px;
  color: ${getColorFromTheme('textBody')};
  cursor: pointer;
  display: flex;
  font: 400 14px/22px Graphik, sans-serif;
  justify-content: space-between;
  min-height: 46px;
  padding: 7px 12px;
  width: 100%;

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    stroke: ${getColorFromTheme('textBody')};
    transition: transform 160ms ease;
    transform: ${({$open}) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

export const Person = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 10px;
  min-width: 0;
`;

export const Initials = styled.span`
  align-items: center;
  background: ${getColorFromTheme('backgroundDark')};
  border-radius: 50%;
  color: ${getColorFromTheme('blackTwo')};
  display: inline-flex;
  flex: none;
  font-size: 12px;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

export const PersonText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  text-align: left;
`;

export const PersonName = styled.span`
  color: ${getColorFromTheme('blackTwo')};
  font: 500 14px/22px Graphik, sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PersonRole = styled.span`
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/18px Graphik, sans-serif;
`;

export const DropdownPanel = styled.div`
  background: ${getColorFromTheme('white')};
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  box-shadow: ${({theme}) => theme.elevation.level2};
  left: 0;
  margin-top: 6px;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 10;
`;

export const SearchBox = styled.div`
  position: relative;
  padding: 12px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  input {
    width: 100%;
    height: 40px;
    padding: 8px 38px 8px 12px;
    border: 1px solid ${getColorFromTheme('borderDark')};
    border-radius: 8px;
    background: ${getColorFromTheme('white')};
    outline: none;
    color: ${getColorFromTheme('blackTwo')};
    font: 400 14px/20px Graphik, sans-serif;

    &:focus {
      border-color: ${getColorFromTheme('purple300')};
      box-shadow: 0 0 0 3px ${getColorFromTheme('purple200')};
    }
  }

  input::placeholder {
    color: ${getColorFromTheme('textBody')};
  }

  svg {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 16px;
    height: 16px;
  }
`;

export const GroupSearchBox = styled(SearchBox)`
  border-bottom: 0;
  margin-bottom: 12px;
  padding: 0;

  svg {
    right: 12px;
    top: 12px;
  }
`;

export const ListViewport = styled.div`
  position: relative;
  min-height: 0;
`;

export const OptionList = styled.div`
  max-height: 184px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px;
  scrollbar-color: ${getColorFromTheme('borderDark')} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: ${getColorFromTheme('borderDark')};
  }
`;

export const ListFade = styled.div<{$position: 'top' | 'bottom'}>`
  position: absolute;
  z-index: 2;
  right: 6px;
  left: 0;
  height: 34px;
  pointer-events: none;
  top: ${({$position}) => ($position === 'top' ? 0 : 'auto')};
  bottom: ${({$position}) => ($position === 'bottom' ? 0 : 'auto')};
  background: ${({$position}) =>
    $position === 'top'
      ? css`linear-gradient(
          180deg,
          ${getColorFromTheme('white')} 0%,
          transparent 100%
        )`
      : css`linear-gradient(
          0deg,
          ${getColorFromTheme('white')} 0%,
          transparent 100%
        )`};
`;

export const GroupListFade = styled(ListFade)`
  height: 18px;
  left: 1px;
  right: 7px;
`;

export const OptionButton = styled.button<{$selected?: boolean}>`
  align-items: center;
  background: ${({$selected}) =>
    $selected
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  border: 0;
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 8px;
  width: 100%;

  &:hover {
    background: ${getColorFromTheme('backgroundDark')};
  }
`;

export const GroupList = styled.div`
  display: grid;
  gap: 8px;
  max-height: 276px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 2px;
  scrollbar-color: ${getColorFromTheme('borderDark')} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: ${getColorFromTheme('borderDark')};
  }
`;

export const GroupRow = styled.label<{$selected?: boolean}>`
  align-items: center;
  background: ${getColorFromTheme('white')};
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 6px;
  color: ${({$selected}) =>
    $selected ? getColorFromTheme('blackTwo') : getColorFromTheme('textBody')};
  cursor: pointer;
  display: grid;
  font: ${({$selected}) => ($selected ? '600' : '400')} 14px/22px Graphik,
    sans-serif;
  gap: 8px;
  grid-template-columns: 16px minmax(0, 1fr) auto;
  min-height: 68px;
  padding: 12px;
  text-align: left;
  width: 100%;

  &:hover,
  &:focus-within {
    outline: none;
    border-color: ${getColorFromTheme('purple300')};
  }
`;

export const GroupCopy = styled.span`
  display: block;
  min-width: 0;
`;

export const GroupName = styled.span`
  display: block;
  overflow: hidden;
  color: inherit;
  font: inherit;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const GroupMeta = styled.span`
  display: block;
  overflow: hidden;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TypePill = styled.span`
  background: ${getColorFromTheme('backgroundDark')};
  border-radius: 8px;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;
  padding: 4px 8px;
`;

export const ReviewList = styled.div`
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 8px;
  overflow: hidden;
`;

export const ReviewRow = styled.div`
  align-items: center;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(140px, 0.8fr);
  padding: 14px;

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 640px) {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }
`;

export const EmailPill = styled.span`
  background: ${getColorFromTheme('backgroundDark')};
  border-radius: 6px;
  color: ${getColorFromTheme('blackTwo')};
  display: inline-block;
  font-size: 12px;
  max-width: 100%;
  overflow: hidden;
  padding: 6px 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const GroupPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const GroupPill = styled.span`
  background: ${getColorFromTheme('backgroundDark')};
  border-radius: 6px;
  color: ${getColorFromTheme('textBody')};
  font-size: 11px;
  padding: 5px 7px;
`;

export const ErrorBanner = styled.div`
  background: ${getColorFromTheme('red100')};
  border: 1px solid ${getColorFromTheme('red300')};
  border-radius: 8px;
  color: ${getColorFromTheme('red400')};
  font-size: 13px;
  margin-top: 16px;
  padding: 10px 12px;
`;

export const DialogFooter = styled.footer`
  align-items: center;
  background: ${getColorFromTheme('white')};
  border-top: 1px solid ${getColorFromTheme('borderLight')};
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px;
`;

const actionButton = css`
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  height: 42px;
  padding: 0 20px;
`;

export const SecondaryButton = styled.button`
  ${actionButton};
  background: ${getColorFromTheme('white')};
  border: 1px solid ${getColorFromTheme('borderDark')};
  color: ${getColorFromTheme('blackTwo')};

  &:hover {
    background: ${getColorFromTheme('backgroundDark')};
  }
`;

export const PrimaryButton = styled.button`
  ${actionButton};
  background: ${getColorFromTheme('purple300')};
  border: 1px solid ${getColorFromTheme('purple300')};
  color: ${getColorFromTheme('alwaysWhite')};

  &:hover:not(:disabled) {
    background: ${getColorFromTheme('purple350')};
  }

  &:disabled {
    background: ${getColorFromTheme('backgroundDark')};
    border-color: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

export const SuccessBody = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 24px 0 8px;
  text-align: center;
`;

export const SuccessList = styled.div`
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 8px;
  margin-top: 20px;
  max-height: 220px;
  overflow: auto;
  text-align: left;
  width: 100%;
`;

export const SuccessRow = styled.div`
  align-items: center;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  display: flex;
  font-size: 13px;
  justify-content: space-between;
  padding: 12px 14px;

  &:last-child {
    border-bottom: 0;
  }
`;

export const SentStatus = styled.span`
  color: ${getColorFromTheme('green400')};
  font-size: 12px;
  font-weight: 500;
`;
